import type { ComponentProps } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { renderRichText } from "@storyblok/react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { useAuth } from "@clerk/nextjs";

import { Button } from "@/components/ui/button";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { Video } from "@/components/media/Video";
import { Avatar } from "@/components/layout/Avatar";
import { Headline } from "@/components/typography/Headline";
import { RichText } from "@/components/typography/RichText";
import type { TextVideoStoryblok } from "@/component-types-sb";
import { cn } from "@/lib/utils";

import { VideoSkeleton } from "@/components/layout/Skeleton";
import { useConsent } from "@/lib/hooks/useConsent";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => <VideoSkeleton className="w-full h-full" />,
});

type TextVideoProps = { blok: TextVideoStoryblok } & ComponentProps<"article">;

const TextVideo = ({ blok, ...rest }: TextVideoProps) => {
  const { userId } = useAuth();
  const { statistics } = useConsent();
  const team = blok.team as any;

  return (
    <>
      {blok.text && blok.headline ? (
        <article
          className={cn(
            "bg-white text-black-95 rounded-3xl flex flex-col-reverse md:flex-row gap-0 md:gap-14 mb-8",
            {
              ["md:flex-row-reverse"]: blok.reverse,
            },
            rest.className
          )}
          {...storyblokEditable(blok)}
        >
          <div className="md:w-2/3 flex p-6 lg:p-8 flex-col items-start md:justify-center">
            <Headline as="h3" size="medium" className="mb-6 md:my-4 font-bold">
              {blok.headline}
            </Headline>

            <RichText
              html={renderRichText(blok.text)}
              className="text-copy mb-4"
            />

            {team && (
              <Avatar
                image={team.content?.image?.filename}
                name={team.content?.subheadline}
                text={team.content?.headline}
              />
            )}

            {blok.link && blok.button && (
              <Link href={getUrlFromLinkObject(blok.link) || "#"}>
                <Button variant="black" size="sm" className="w-full md:w-auto">
                  <ArrowRight size="sm" />
                  {blok.button}
                </Button>
              </Link>
            )}
          </div>
          <div className="md:w-1/3 relative">
            <div className="h-48 md:h-full md:max-h-full overflow-hidden rounded-3xl flex items-center justify-center">
              <Video fallback="/images/shape.svg" className="object-contain">
                <source src={blok.source} type="video/webm" />
                {blok.source && (
                  <source src={blok.source.replace(/\.webm$/, ".mp4")} type="video/mp4" />
                )}
              </Video>
            </div>
          </div>
        </article>
      ) : (
        <article
          className={cn("bg-white text-black-95 rounded-3xl md:p-6 mb-8 overflow-hidden", rest.className)}
        >
          <MuxPlayer
            streamType="on-demand"
            playbackId={blok.playback_id}
            {...(blok.thumbnail?.filename && { poster: blok.thumbnail.filename })}
            {...(blok.thumbnail_time && { thumbnailTime: blok.thumbnail_time })}
            metadata={{
              video_title: blok.title,
              viewer_user_id: userId,
            }}
            preload="auto"
            forwardSeekOffset={0}
            backwardSeekOffset={0}
            className="aspect-video rounded-3xl md:rounded-xl"
            style={{ "--media-border-radius": "24px" } as React.CSSProperties}
            disableCookies={!statistics}
            accentColor="#D3C4AC"
          />
        </article>
      )}
    </>
  );
};

export default TextVideo;
